import React, { useEffect, useState } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import './SetupProcess.css'
import PropertySelect from './selectYourLocation/PropertySelect'
import FinishAccountSetup from './finishAccountSetup/FinishAccountSetup'
import GetTheApp from './getTheApp/GetTheApp'
import { useHistory, useLocation } from 'react-router-dom'
import { UserSettingsApi } from '../apis/UserSettingsApi'
import { useData } from '../data/DataProvider'
import { useAuth } from '../auth/Auth'
import RangeSelect from './selectYourVehicle/SelectRange'
import FundsSelect from './selectFunds/SelectFunds'
import FundsSelectRecurring from './selectFunds/SelectFundsRecurring'
import { JoinPropertyApi } from '../apis/JoinPropertyApi'
import { STRIPE_API_KEY } from '../constants'
import { useCookies } from 'react-cookie'
import { GpStepper } from '../components/gp'

const STEP_DEFS = [
    { key: 'property', label: 'Property', icon: 'home' },
    { key: 'mileage', label: 'Driving', icon: 'bolt' },
    { key: 'funds', label: 'Funds', icon: 'wallet' },
    { key: 'done', label: 'Download', icon: 'qr' },
]

const stripePromise = loadStripe(STRIPE_API_KEY)

const SetupProcess = () => {
    const [activeKey, setActiveKey] = useState('property')
    const [selectedProperty, setSelectedProperty] = useState({})
    const [milesPerWeek, setMilesPerWeek] = useState(50)
    const [funds, setFunds] = useState(25)
    const [isRecurring, setIsRecurring] = useState(false)
    const [nextBillDate, setNextBillDate] = useState()
    const [recurringFees, setRecurringFees] = useState([])
    // eslint-disable-next-line no-unused-vars
    const [paymentMethodId, setPaymentMethodId] = useState(undefined)
    const [, , removeCookie] = useCookies(['propertyUuid', 'inviteUuid'])
    const auth = useAuth()
    const data = useData()
    const location = useLocation()
    const history = useHistory()

    const propertyPath = '/setup/select-property'
    const rangePath = '/setup/select-range'
    const fundsPath = '/setup/select-funds'
    const paymentPath = '/setup/payment-info'
    const downloadAppPath = '/setup/app-download'

    const handleNext = (nextPath) => {
        history.push(nextPath)
        document.querySelector('body').scrollTo(0, 0)
    }

    const propertySelected = (propertyInfo) => {
        setSelectedProperty(propertyInfo)
        if (propertyInfo.billingModel.energyModel === 'RECURRING') {
            setIsRecurring(true)
            setRecurringFees(propertyInfo.billingModel.fees)
            setFunds(propertyInfo.billingModel.fees[0].price)
            setNextBillDate(propertyInfo.billingModel.nextBillDate)
            handleNext(fundsPath)
        } else {
            handleNext(rangePath)
        }
    }

    const milesSelected = (miles) => {
        setMilesPerWeek(miles)
        handleNext(fundsPath)
    }

    const fundsSelected = (selectedFunds) => {
        setFunds(selectedFunds)
        handleNext(paymentPath)
    }

    const creditCardAdded = async () => {
        if (auth.isConsumerGuest()) {
            await UserSettingsApi.put({
                milesPerWeek: 0,
                topUpAmount: 0,
                initialSetupComplete: true,
            })
            setupComplete()
        } else {
            if (selectedProperty.propertyUUID) {
                await JoinPropertyApi.post(selectedProperty.propertyUUID)
            }
            if (isRecurring && funds) {
                await UserSettingsApi.put({
                    milesPerWeek: 0,
                    topUpAmount: funds,
                    initialSetupComplete: true,
                })
            } else if (milesPerWeek && funds) {
                await UserSettingsApi.put({
                    milesPerWeek: milesPerWeek,
                    topUpAmount: funds * 100,
                    initialSetupComplete: true,
                })
            }
            handleNext(downloadAppPath)
        }
    }

    const setupComplete = () => {
        removeCookie('propertyUuid', { path: '/' })
        removeCookie('inviteUuid', { path: '/' })
        data.setIsSetupComplete(true)
        data.loadInitialData()
        history.push('/dashboard')
    }

    useEffect(() => {
        const p = location.pathname
        if (p.endsWith(propertyPath)) setActiveKey('property')
        else if (p.endsWith(rangePath)) setActiveKey('mileage')
        else if (p.endsWith(fundsPath) || p.endsWith(paymentPath)) setActiveKey('funds')
        else if (p.endsWith(downloadAppPath)) setActiveKey('done')
    }, [location.pathname])

    const stepTitle = (() => {
        switch (activeKey) {
            case 'property':
                return { title: 'Find your property', subtitle: "Tell us where you'll be charging. We'll match it to a GoPower-equipped property and request access for you." }
            case 'mileage':
                return { title: 'Weekly driving', subtitle: 'Roughly how many miles do you drive each week? This helps us recommend a top-up amount.' }
            case 'funds':
                if (location.pathname.endsWith(paymentPath)) {
                    return { title: 'Payment details', subtitle: 'Add a card for your wallet. You can change this any time from Manage Billing.' }
                }
                return { title: 'Recommended funds', subtitle: 'Choose a top-up amount that fits your driving. We can auto-reload your wallet when it gets low.' }
            case 'done':
                return { title: "You're all set", subtitle: "Download the GoPower app to start charging. We've notified your property manager — once approved, you can plug in and go." }
            default:
                return { title: '' }
        }
    })()

    const isGuest = auth.isConsumerGuest()
    const onPayment = location.pathname.endsWith(paymentPath)
    const onProperty = location.pathname.endsWith(propertyPath)
    const onRange = location.pathname.endsWith(rangePath)
    const onFunds = location.pathname.endsWith(fundsPath)
    const onDownload = location.pathname.endsWith(downloadAppPath)

    return (
        <div className="setup-page-container">
            <div className="gp-setup-card">
                {!isGuest && <GpStepper steps={STEP_DEFS} active={activeKey} />}

                <div className="gp-setup-title-wrap">
                    <h1 className="gp-setup-title">{stepTitle.title}</h1>
                    {stepTitle.subtitle && (
                        <p className="gp-setup-subtitle">{stepTitle.subtitle}</p>
                    )}
                </div>

                {isGuest && onPayment && (
                    <Elements stripe={stripePromise}>
                        <FinishAccountSetup
                            isGuestCharging
                            name={auth?.user?.attributes?.name}
                            creditCardAdded={creditCardAdded}
                            funds={funds}
                            isRecurring={isRecurring}
                            nextBillDate={nextBillDate}
                            setPaymentMethodId={setPaymentMethodId}
                        />
                    </Elements>
                )}

                {!isGuest && (
                    <>
                        {onProperty && (
                            <PropertySelect
                                selectProperty={propertySelected}
                                selectedProperty={selectedProperty}
                            />
                        )}
                        {onRange && (
                            <RangeSelect
                                selectMilesPerWeek={milesSelected}
                                milesPerWeek={milesPerWeek}
                            />
                        )}
                        {onFunds && !isRecurring && (
                            <FundsSelect
                                milesPerWeek={milesPerWeek}
                                fundsSelected={fundsSelected}
                                funds={funds}
                                selectedProperty={selectedProperty}
                            />
                        )}
                        {onFunds && isRecurring && (
                            <FundsSelectRecurring
                                recurringFees={recurringFees}
                                fundsSelected={fundsSelected}
                                funds={funds}
                            />
                        )}
                        {onPayment && (
                            <Elements stripe={stripePromise}>
                                <FinishAccountSetup
                                    creditCardAdded={creditCardAdded}
                                    funds={funds}
                                    isRecurring={isRecurring}
                                    nextBillDate={nextBillDate}
                                    setPaymentMethodId={setPaymentMethodId}
                                />
                            </Elements>
                        )}
                        {onDownload && <GetTheApp setupComplete={setupComplete} />}
                    </>
                )}
            </div>
        </div>
    )
}

export default SetupProcess
